import { memo, useEffect } from 'react';

import { Button, Flex, Heading, Input, Label, Separator, Text, TextField } from '@/components/core';
import { toProfileId, useGetProfile } from '@/hooks/api';

import { Step } from '../agents';
import { useWizard } from './Wizard';

const isValidUrl = (url?: string): boolean => {
  if (!url) return false;

  if (!url.startsWith('http')) {
    url = `https://${url}`;
  }

  try {
    const parsedUrl = new URL(url);
    const parts = parsedUrl.hostname.split('.');
    return parts.length > 1 && parts[parts.length - 1].length > 1;
  } catch {
    return false;
  }
};

const getUrlFromEmail = (email?: string) => {
  if (!email) return;

  const hostname = email.split('@')?.[1];
  const disallowed = ['gmail', 'hotmail', 'microsoft', 'live', 'hotmail'];
  if (disallowed.some((d) => hostname?.toLowerCase()?.includes(d))) {
    return;
  }

  return hostname;
};

type ContactDetailsProps = object;

// eslint-disable-next-line no-empty-pattern
const IContactDetails = ({}: ContactDetailsProps) => {
  const wizard = useWizard();

  const { data: profile } = useGetProfile(toProfileId('me'));

  useEffect(() => {
    if (profile != null) {
      wizard.state.firstName = wizard.state.firstName || profile?.givenName || '';
      wizard.state.lastName = wizard.state.lastName || profile?.familyName || '';
      wizard.state.organizationUrl = wizard.state.organizationUrl || getUrlFromEmail(profile?.email) || '';
    }
  }, [profile, wizard.state]);

  const isDisabled = !wizard.state.firstName || !wizard.state.lastName || !wizard.state.organizationName;

  return (
    <Step className="gap-md w-110">
      <Flex col variant="plain" className="px-md">
        <Flex col className="gap-md" key={profile?.profileId}>
          <Heading size="md">👋 Hello there, new friend!</Heading>

          <Flex className="gap-md">
            <TextField
              onChange={(v) => (wizard.state.firstName = v)}
              defaultValue={wizard.state.firstName || profile?.givenName || ''}
              className="w-40"
            >
              <Label isRequired>First name</Label>
              <Input placeholder="John" />
            </TextField>

            <TextField
              onChange={(v) => (wizard.state.lastName = v)}
              defaultValue={wizard.state.lastName || profile?.familyName || ''}
              className="grow"
            >
              <Label isRequired>Last name</Label>
              <Input placeholder="Doe" />
            </TextField>
          </Flex>

          <TextField
            onChange={(v) => (wizard.state.organizationName = v)}
            defaultValue={wizard.state.organizationName}
            isRequired
          >
            <Label isRequired>Organization name</Label>
            <Input placeholder="MyCompany Inc" />
          </TextField>

          <TextField
            onChange={(v) => (wizard.state.organizationUrl = v)}
            defaultValue={wizard.state.organizationUrl || getUrlFromEmail(profile?.email)}
          >
            <Label>Organization URL</Label>
            <Input placeholder="mycompany.com" />
          </TextField>
        </Flex>
      </Flex>

      <Separator orientation="horizontal" />

      <Flex col variant="plain" className="px-md">
        <Button variant="primary" onPress={() => wizard.next({ ...wizard.state })} isDisabled={isDisabled}>
          <Text>Continue</Text>
        </Button>
      </Flex>
    </Step>
  );
};

export const ContactDetails = memo(IContactDetails) as typeof IContactDetails;
